import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ORGANIZATION_SCHEMA } from '@/lib/seo';
import type { Job } from '@/types/job';

function jobPostingSchema(job: Job) {
  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title,
    description: job.description,
    employmentType: 'FULL_TIME',
    hiringOrganization: {
      '@type': 'Organization',
      name: 'Xpend Marketing',
      sameAs: 'https://www.xpendmarketing.com',
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Tel Aviv',
        addressCountry: 'IL',
      },
    },
  };
}

export default function StructuredData() {
  const { pathname } = useLocation();
  const isCareers = pathname === '/careers';
  const jobMatch = pathname.match(/^\/careers\/(\d+)$/);
  const isJobDetail = !!jobMatch;
  const [jobs, setJobs] = useState<Job[]>([]);
  const [singleJob, setSingleJob] = useState<Job | null>(null);

  useEffect(() => {
    if (!isCareers) {
      setJobs([]);
      return;
    }

    let cancelled = false;

    async function loadJobs() {
      try {
        const res = await fetch('/api/jobs');
        if (!res.ok) return;
        const data: unknown = await res.json();
        if (!cancelled && Array.isArray(data)) {
          setJobs(data as Job[]);
        }
      } catch {
        if (!cancelled) setJobs([]);
      }
    }

    void loadJobs();
    return () => { cancelled = true; };
  }, [isCareers]);

  useEffect(() => {
    if (!isJobDetail) {
      setSingleJob(null);
      return;
    }

    let cancelled = false;

    fetch(`/api/jobs/${jobMatch[1]}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data: Job | null) => {
        if (!cancelled && data) setSingleJob(data);
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [isJobDetail, jobMatch?.[1]]);

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(ORGANIZATION_SCHEMA)}
      </script>
      {isCareers &&
        jobs.map((job) => (
          <script key={job.id} type="application/ld+json">
            {JSON.stringify(jobPostingSchema(job))}
          </script>
        ))}
      {isJobDetail && singleJob && (
        <script type="application/ld+json">
          {JSON.stringify(jobPostingSchema(singleJob))}
        </script>
      )}
    </Helmet>
  );
}
